import React, { useEffect } from 'react';
import SlideoutLayout from '../../layout/slideout/SlideoutLayout';
import DisableKeysForm from '../components/DisableKeysForm';
import { getKeyProviders } from '../../../actions/keyProviderActions';
import { disableKeys } from '../../../actions/keyCodeActions';
import { useForm } from 'react-hook-form';
import Loading from '../../common/Loading';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { fetchDisableReasons } from '../../../reducers/disableReasonReducer';

export interface DisableKeysProps {
  title?: string;
  done: () => void;
  cancel: () => void;
  targetGameId?: string | undefined;
  targetGameSKUId?: string | undefined;
  targetCompanyId?: string | undefined;
  targetKeyWorkflowId?: string | undefined;
  targetRequestingUserProfileId?: string | undefined;
  targetAccountDetailId?: string | undefined;
  targetKeyRequestId?: string | undefined;
  targetKeyRequestAccountDetail?: string | undefined | any[];
  defaultReason?: string | undefined;
}

function DisableKeys({
  title,
  done,
  cancel,
  targetGameId,
  targetGameSKUId,
  targetCompanyId,
  targetKeyWorkflowId,
  targetRequestingUserProfileId,
  targetAccountDetailId,
  targetKeyRequestId,
  targetKeyRequestAccountDetail,
  defaultReason,
}: DisableKeysProps) {
  const disableReasons = useAppSelector((state) => state.disableReasons);
  const keyProviders = useAppSelector((state) => state.keyProviders);
  const dispatch = useAppDispatch();

  const {
    register,
    formState: { errors },
    handleSubmit,
    control,
    setValue,
    getValues,
  } = useForm();
  const { t } = useTranslation();

  const onSubmit = (data: any) => {
    const targetAccountDetails =
      Array.isArray(targetKeyRequestAccountDetail) &&
      targetKeyRequestAccountDetail.length > 0
        ? targetKeyRequestAccountDetail
        : undefined;

    const _disable = (keyProviderId: string | null) =>
      disableKeys({
        keyProviderId: keyProviderId ?? undefined,
        reason: data.reason,
        disabledReasons: data.disabledReasons,
        description: data.description,
        targetGameId,
        targetGameSKUId,
        targetCompanyId,
        targetKeyWorkflowId,
        targetRequestingUserProfileId,
        targetAccountDetailId,
        targetKeyRequestId,
        targetKeyRequestAccountDetail: targetAccountDetails,
        targetIsAvailable: data.codeStatuses?.some((x: number) => x === 1),
        targetIsAllocated: data.codeStatuses?.some((x: number) => x === 2),
        targetIsActivated: data.codeStatuses?.some((x: number) => x === 4),
        targetIsSold: data.codeStatuses?.some((x: number) => x === 8),
        targetIsExpired: data.codeStatuses?.some((x: number) => x === 16),
      });

    if (data.keyProviders) {
      Promise.all(
        data.keyProviders.map((keyProviderId: string) => _disable(keyProviderId))
      )
        .then((xx) => {
          const allErrors = xx.every((x: any) => x.isError);
          if (allErrors) {
            toast.error(
              t('Disabled keys request failed') +
              '\n' +
              xx.map((x: any) => x.message + '\n')
            );
          } else {
            const withErrors = xx.some((x: any) => x.isError);
            if (withErrors) {
              toast.warning(
                t('Disable keys request queues, with errors') +
                '\n' +
                xx.filter((x: any) => x.isError).map((x: any) => x.message + '\n')
              );
            } else {
              toast.success(t('Disabled keys request successfully queued'));
              done();
            }
          }
        })
        .catch((e) => {
          toast.error(e.message || t('Failed to queue disabled keys request'));
        });
    } else {
      _disable(null)
        .then((response: any) => {
          if (response.isSuccess) {
            toast.success(t('Disabled keys request successfully queued'));
          } else {
            toast.error(response.message);
          }
          done();
        })
        .catch((e) => {
          toast.error(e.message || t('Failed to queue disabled keys request'));
        });
    }
  };

  useEffect(() => {
    if (!keyProviders) {
      dispatch(getKeyProviders());
    }
  }, [keyProviders, dispatch]);

  useEffect(() => {
    if (!disableReasons) {
      dispatch(fetchDisableReasons());
    }
  }, [disableReasons, dispatch]);

  return keyProviders && disableReasons ? (
    <SlideoutLayout
      title={title || t('Disabled keys')}
      cancel={cancel}
    >
      <DisableKeysForm
        showKeyProviders={
          targetKeyRequestId !== undefined ||
          targetGameId !== undefined ||
          targetGameSKUId !== undefined ||
          targetKeyRequestAccountDetail !== undefined ||
          targetAccountDetailId !== undefined
        }
        keyProviders={keyProviders}
        disableReasons={disableReasons}
        register={register}
        errors={errors}
        control={control}
        cancel={cancel}
        submit={handleSubmit(onSubmit)}
        setValue={setValue}
        getValues={getValues}
        defaultReason={defaultReason}
      />
    </SlideoutLayout>
  ) : (
    <Loading />
  );
}


export default DisableKeys;
